import {
  EFIRMA_REUSE_MISSING_METADATA_MESSAGE,
  buildSatRequestSemantics,
  normalizeCfdiRunStatus,
  normalizeEfirmaProfile,
  validateCfdiRunStatusShape,
  validateCfdiSecurePayload
} from '../utils/cfdiRequest';

const N8N_WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL;
export const N8N_ARCHIVOS_WEBHOOK_URL = process.env.REACT_APP_N8N_ARCHIVOS_URL;
const N8N_LOGS_URL =
  process.env.REACT_APP_N8N_LOGS_URL ||
  process.env.REACT_APP_N8N_LOGS_UR ||
  '';
const DEV_CFDI_RUNTIME_HOST = 'dev.ci.acnegocios.mx';
const MONTH_LABELS_ES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre'
];
const ACTIVIDADES_MENSUALES_TABLE_ID = 'tblDmlaqfFPqo1Keh';
const ACTIVIDADES_MENSUALES_FIELD_IDS = {
  actividad: 'fldeAr1nJNAO8GQml',
  numero: 'fldtUNclvh3edt8lQ',
  dia: 'fldcud6D49CL1uTpa',
  fechaProgramada: 'fldjBM1oKw1JMVx8y',
  notasInternas: 'fldBEP9uwrnAT2VJS',
  requiereReporte: 'fldMlAVqQMzitrSWe',
  automatizable: 'fldaOmUPC4G9QHH7X',
  fechaRealizada: 'fldQ76BtN1S9wlZJP',
  estatus: 'fldRbs0YGkDofBjfD',
  tipoActividad: 'fldqaLHPnDH4A3AEn',
  subirArchivo: 'fldcfSUv2HLhCV9Xd',
  prioridad: 'flddbdFvlEsueuwIg',
  calendario: 'fldTqNRnugrtsFegd',
  clientes: 'fldxfcHpvDbvc5UiC',
  obligacion: 'fldlH81bL9Gm9omwp',
  responsable: 'fld71UyNyrUATq4Rp'
};

function getEnvValue(...names) {
  for (const name of names) {
    const value = String(process.env[name] || '').trim();
    if (value) return value;
  }
  return '';
}

function getRuntimeHostname() {
  const overrideHostname = getEnvValue('REACT_APP_CFDI_RUNTIME_HOST_OVERRIDE');
  if (overrideHostname) {
    return overrideHostname.toLowerCase();
  }
  if (typeof window === 'undefined' || !window.location) {
    return '';
  }
  return String(window.location.hostname || '').trim().toLowerCase();
}

function shouldUseDevCfdiRoutes() {
  return getRuntimeHostname() === DEV_CFDI_RUNTIME_HOST;
}

function getCfdiEndpointConfig() {
  const requestUrl = getEnvValue('REACT_APP_N8N_CFDI_REQUEST_URL', 'REACT_APP_N8N_CFDI_WEBHOOK_URL');
  const statusUrl = getEnvValue('REACT_APP_N8N_CFDI_STATUS_URL');
  const preflightUrl = getEnvValue('REACT_APP_N8N_CFDI_PREFLIGHT_URL');
  const efirmaControlUrl = getEnvValue('REACT_APP_N8N_EFIRMA_CONTROL_URL');
  const efirmaStatusUrl = getEnvValue('REACT_APP_N8N_EFIRMA_STATUS_URL');

  if (!shouldUseDevCfdiRoutes()) {
    return {
      requestUrl,
      statusUrl,
      preflightUrl,
      efirmaControlUrl,
      efirmaStatusUrl,
      routeMode: 'default'
    };
  }

  return {
    requestUrl: getEnvValue('REACT_APP_N8N_CFDI_DEV_REQUEST_URL') || requestUrl,
    statusUrl: getEnvValue('REACT_APP_N8N_CFDI_DEV_STATUS_URL') || statusUrl,
    preflightUrl: getEnvValue('REACT_APP_N8N_CFDI_DEV_PREFLIGHT_URL') || preflightUrl,
    efirmaControlUrl: getEnvValue('REACT_APP_N8N_EFIRMA_DEV_CONTROL_URL') || efirmaControlUrl,
    efirmaStatusUrl: getEnvValue('REACT_APP_N8N_EFIRMA_DEV_STATUS_URL') || efirmaStatusUrl,
    routeMode: 'dev'
  };
}

function resolveWebhookUrl(explicitUrl) {
  const resolved = explicitUrl || N8N_WEBHOOK_URL || N8N_ARCHIVOS_WEBHOOK_URL;
  if (!resolved) {
    throw new Error('Configura REACT_APP_N8N_WEBHOOK_URL o REACT_APP_N8N_ARCHIVOS_URL para enviar archivos al flujo de n8n');
  }
  return resolved;
}

function resolveCfdiRequestUrl(explicitUrl) {
  const resolved = explicitUrl || getCfdiEndpointConfig().requestUrl;
  if (!resolved) {
    throw new Error('Configura REACT_APP_N8N_CFDI_REQUEST_URL para enviar solicitudes CFDI seguras');
  }
  return resolved;
}

function resolveCfdiPreflightUrl(explicitUrl) {
  return explicitUrl || getCfdiEndpointConfig().preflightUrl || '';
}

export function getCfdiIntegrationConfigError() {
  const { requestUrl, statusUrl } = getCfdiEndpointConfig();
  if (!requestUrl && !statusUrl) {
    return 'Configura REACT_APP_N8N_CFDI_REQUEST_URL y REACT_APP_N8N_CFDI_STATUS_URL para habilitar CFDI.';
  }
  if (!requestUrl) {
    return 'Configura REACT_APP_N8N_CFDI_REQUEST_URL para habilitar el envio CFDI.';
  }
  if (!statusUrl) {
    return 'Configura REACT_APP_N8N_CFDI_STATUS_URL para habilitar la consulta de estado CFDI.';
  }
  return '';
}

export function isCfdiIntegrationConfigured() {
  return !getCfdiIntegrationConfigError();
}

function buildPreflightUnavailableStatus({ endpoint = '', reason = '', status = 0, message = '' } = {}) {
  const normalizedReason = String(reason || 'PREFLIGHT_UNAVAILABLE').trim();
  const normalizedMessage = String(message || '').trim();
  const detail = [`reason=${normalizedReason}`];
  if (status) detail.push(`status=${status}`);
  if (endpoint) detail.push(`endpoint=${endpoint}`);
  if (normalizedMessage) detail.push(`message=${normalizedMessage}`);

  return {
    checked: true,
    ready: false,
    backend: '',
    strictRealOnly: true,
    blockers: [
      {
        id: 'PREFLIGHT_UNAVAILABLE',
        summary: 'No fue posible validar el preflight SAT para este entorno.',
        action: 'Verifica el endpoint de preflight DEV y que el workflow esté activo en n8n.',
        details: detail
      }
    ],
    reason: normalizedReason,
    raw: null
  };
}

export async function fetchCfdiBackendPreflight({ preflightUrl } = {}) {
  const endpoint = resolveCfdiPreflightUrl(preflightUrl);
  if (!endpoint) {
    return buildPreflightUnavailableStatus({
      reason: 'preflight_url_missing'
    });
  }

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });
    const data = await parseFetchResponse(response);
    const preflight =
      data?.preflight && typeof data.preflight === 'object'
        ? data.preflight
        : data && typeof data === 'object'
          ? data
          : {};

    return {
      checked: true,
      ready: Boolean(preflight?.ready),
      backend: String(preflight?.backend || '').trim().toLowerCase(),
      strictRealOnly: Boolean(preflight?.strictRealOnly),
      blockers: Array.isArray(preflight?.blockers) ? preflight.blockers : [],
      reason: '',
      raw: preflight
    };
  } catch (error) {
    const rawReason = String(error?.errorCode || error?.code || '').trim();
    const normalizedReason = /^[A-Z_]+$/.test(rawReason) ? rawReason : 'PREFLIGHT_UNAVAILABLE';
    return buildPreflightUnavailableStatus({
      endpoint,
      reason: normalizedReason,
      status: Number(error?.status || 0),
      message: error?.message || ''
    });
  }
}

function resolveEfirmaControlUrl(explicitUrl) {
  const resolved = explicitUrl || getCfdiEndpointConfig().efirmaControlUrl;
  if (!resolved) {
    throw new Error('Configura REACT_APP_N8N_EFIRMA_CONTROL_URL para controlar eFirma.');
  }
  return resolved;
}

export function getEfirmaIntegrationConfigError() {
  if (!getCfdiEndpointConfig().efirmaControlUrl) {
    return 'Configura REACT_APP_N8N_EFIRMA_CONTROL_URL para registrar eFirma.';
  }
  return '';
}

export function getEfirmaControlConfigError() {
  if (!getCfdiEndpointConfig().efirmaControlUrl) {
    return 'Configura REACT_APP_N8N_EFIRMA_CONTROL_URL para registrar eFirma.';
  }
  return '';
}

export function getEfirmaStatusConfigError() {
  if (!getCfdiEndpointConfig().efirmaStatusUrl) {
    return 'Configura REACT_APP_N8N_EFIRMA_STATUS_URL para consultar la vigencia de eFirma.';
  }
  return '';
}

export function isEfirmaIntegrationConfigured() {
  return Boolean(getCfdiEndpointConfig().efirmaControlUrl);
}

export function isEfirmaControlConfigured() {
  return Boolean(getCfdiEndpointConfig().efirmaControlUrl);
}

export function isEfirmaStatusConfigured() {
  return Boolean(getCfdiEndpointConfig().efirmaStatusUrl);
}

async function parseFetchResponse(response) {
  const rawBody = await response.text();
  if (!response.ok) {
    let parsedBody = null;
    try {
      parsedBody = rawBody ? JSON.parse(rawBody) : null;
    } catch {
      parsedBody = null;
    }
    const errorCode = parsedBody?.errorCode || parsedBody?.code || '';
    const errorMessage =
      parsedBody?.message ||
      parsedBody?.error ||
      (rawBody ? `n8n error ${response.status}: ${rawBody}` : `n8n error ${response.status}`);
    const error = new Error(errorMessage);
    if (errorCode) {
      error.code = errorCode;
      error.errorCode = errorCode;
    }
    error.status = response.status;
    error.payload = parsedBody;
    throw error;
  }
  if (!rawBody) return {};
  try {
    return JSON.parse(rawBody);
  } catch {
    return { message: rawBody };
  }
}

const TEXT_METADATA_KEYS = new Set(['tipo', 'itemcount']);
const RANGE_ALIAS = {
  semana: 'week',
  mes: 'month',
  anio: 'year'
};

function parseLogsFromPlainText(text) {
  if (!text) return [];
  const lines = text
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line);

  const entries = [];
  let current = null;
  const pushCurrent = () => {
    if (current && Object.keys(current).length) {
      entries.push(current);
    }
    current = null;
  };

  for (const line of lines) {
    if (/^\d+$/.test(line)) {
      pushCurrent();
      current = {};
      continue;
    }

    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) {
      continue;
    }

    const key = line.slice(0, colonIndex).trim();
    const lowerKey = key.toLowerCase();
    if (TEXT_METADATA_KEYS.has(lowerKey)) {
      pushCurrent();
      continue;
    }

    const value = line.slice(colonIndex + 1).trim();
    if (!current) {
      current = {};
    }
    current[key] = value;
    current[lowerKey] = value;
  }

  pushCurrent();
  return entries.filter(entry => Boolean(entry.id || entry.executionid || entry.execution_id));
}

function getLogsArrayFromPayload(payload) {
  if (!payload) return null;
  if (Array.isArray(payload)) return payload;
  const candidates = [
    payload.data,
    payload.logs,
    payload.items,
    payload.results,
    payload.rows,
    payload.executions,
    payload.executionsList,
    payload.executions_list
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  return null;
}

const capitalize = value => {
  if (!value) return '';
  const str = String(value);
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const normalizeObligationOption = value => {
  const upper = (value || '').toUpperCase();
  if (!upper) return '';
  if (upper.includes('NOM')) return 'NOMINA';
  if (upper.includes('SUA')) return 'SUA';
  if (upper.includes('ISN')) return 'ISN';
  if (upper.includes('DIOT')) return 'DIOT';
  if (upper.includes('CE')) return 'CE';
  if (upper.includes('SAT')) return 'SAT';
  return value || '';
};

const sanitizeFolderName = value => {
  if (!value) return '';
  return String(value)
    .replace(/[\\/:"*?<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 180);
};

const getMonthName = month => {
  const index = Number(month) - 1;
  if (Number.isNaN(index) || index < 0 || index > 11) {
    return '';
  }
  return MONTH_LABELS_ES[index] || '';
};

const buildActividadesMensualesPayload = (request, periodInfo = {}, directoryInfo = {}) => {
  const actividad = request?.activity || request?.actividad || request?.obligation || request?.service || '';
  const activityKey = request?.activityKey || request?.service || request?.obligation || '';
  const activityKeySafe = sanitizeFolderName(activityKey);
  const activityId = request?.activityId || request?.id || '';
  const activityNumber = request?.activityNumber || request?.no || request?.numero || '';
  const activityPeriod = request?.activityPeriod || periodInfo.period || '';
  const activityFolder = request?.activityFolder || activityKey || actividad;
  const activityFolderSafe = sanitizeFolderName(activityFolder || activityKey || actividad);
  const dueText = request?.dueText || request?.dueLabel || request?.dueDate || '';
  const fechaProgramada = request?.dueDate || request?.fechaProgramada || '';
  const statusValue = request?.status || request?.statusKey || '';
  const obligationValue = request?.obligation || request?.service || '';
  const normalizedObligation = normalizeObligationOption(obligationValue);
  const calendarioRecordId = request?.calendarioRecordId || request?.recordId || request?.airtableRecordId || '';
  const clienteRecordId = directoryInfo?.id || request?.clienteAsociadoId || '';
  const clienteLinkId =
    request?.listaCtesIdCliente || request?.clientId || directoryInfo?.idCliente || '';
  const responsableValor = request?.responsable || request?.assignedTo || '';
  const hasValues =
    actividad ||
    activityKey ||
    calendarioRecordId ||
    request?.requiresUpload ||
    statusValue ||
    normalizedObligation ||
    dueText ||
    activityPeriod ||
    activityFolder;

  const payload = {
    tableId: ACTIVIDADES_MENSUALES_TABLE_ID,
    fieldIds: ACTIVIDADES_MENSUALES_FIELD_IDS
  };

  if (!hasValues) {
    return payload;
  }

  return {
    ...payload,
    values: {
      actividad,
      activityKey,
      activityKeySafe,
      activityId,
      numero: activityNumber,
      activityFolder,
      activityFolderSafe,
      dia: dueText,
      fechaProgramada,
      fechaRealizada: request?.fechaRealizada || '',
      notasInternas: request?.notes || '',
      requiereReporte: request?.requiresReport,
      automatizable: request?.automatizable,
      subirArchivo: Boolean(request?.requiresUpload),
      prioridad: request?.priority || '',
      estatus: statusValue,
      tipoActividad: request?.tipoActividad || request?.activityType || '',
      obligacion: normalizedObligation,
      obligacionRaw: obligationValue,
      calendarioRecordId,
      calendarioTableId: request?.calendarioTableId || '',
      activityPeriod,
      activityPeriodYear: periodInfo.periodYear,
      activityPeriodMonth: periodInfo.periodMonth,
      activityPeriodFolder: periodInfo.periodFolder,
      clientesRecordId: clienteLinkId,
      clienteAsociadoRecordId: clienteRecordId,
      clienteAsociadoIdCliente: directoryInfo?.idCliente || clienteLinkId,
      clienteNombre: request?.cliente || request?.empresa || directoryInfo?.empresa || '',
      clienteAlias: request?.clienteAlias || directoryInfo?.alias || '',
      responsable: responsableValor
    }
  };
};

function buildMetadataPayload(request, extra = {}) {
  const { fileNames = [], fileCount = 0, hasFiles = false, ...restExtra } = extra || {};
  const extractPeriodInfo = source => {
    const candidates = [
      source?.period,
      source?.periodo,
      source?.periodKey,
      source?.activityPeriod,
      source?.metadata?.period,
      source?.metadata?.periodo
    ].filter(Boolean);
    const raw = candidates.find(value => typeof value === 'string' && value.trim()) || '';
    const normalized = raw.trim();
    const match = normalized.match(/(\d{4})-(\d{2})/);
    const periodYear = match ? match[1] : '';
    const periodMonth = match ? match[2] : '';
    return {
      period: normalized,
      periodYear,
      periodMonth,
      periodFolder: periodYear && periodMonth ? `${periodYear}/${periodMonth}` : periodYear || ''
    };
  };

  if (!request) return extra;
  const {
    folio,
    cliente,
    empresa,
    clienteAlias,
    total,
    responsable,
    actualizado,
    contacto,
    clienteAsociadoId,
    airtableRecordId,
    clienteAsociadoInfo,
    service,
    obligation,
    folder,
    dueDate,
    dueLabel,
    dueText,
    metadata: serviceMetadata = {},
    formInputs = [],
    serviceFieldLabels = {},
    emailRecipients = [],
    notificationPreview = '',
    uploadPolicy = {},
    fileStatus,
    existingFileStatus,
    existingFileRecordId
  } = request;
  const periodInfo = extractPeriodInfo(request);
  const directoryInfo = clienteAsociadoInfo || null;
  const directoryEmpresa = directoryInfo?.empresa || '';
  const directoryAlias = directoryInfo?.alias || '';
  const directoryName = directoryInfo?.name || '';
  const directoryRecordId = directoryInfo?.id || clienteAsociadoId || '';
  const preferredClientId =
    typeof request?.listaCtesIdCliente === 'string' && request.listaCtesIdCliente
      ? request.listaCtesIdCliente
      : '';
  const directoryClientId =
    directoryInfo?.idCliente ||
    preferredClientId ||
    (typeof folio === 'string' && folio.includes('-') ? folio.split('-')[0] : folio?.slice(0, 5) || '') ||
    '';
  const empresaValue = directoryEmpresa || (Array.isArray(empresa) ? (empresa[0] || '') : empresa);
  const aliasValue = directoryAlias || (Array.isArray(clienteAlias) ? (clienteAlias[0] || '') : clienteAlias);
  const empresaFolderName = [empresaValue, directoryClientId].filter(Boolean).join(' - ');
  const actividadMensual = buildActividadesMensualesPayload(request, periodInfo, directoryInfo);
  const periodMonthName = getMonthName(periodInfo.periodMonth);
  const periodMonthFolderName = capitalize(periodMonthName) || periodInfo.periodMonth;
  const activityFolderName =
    actividadMensual?.values?.activityFolder ||
    actividadMensual?.values?.activityKey ||
    request?.activityKey ||
    request?.service ||
    request?.obligation ||
    '';
  const clientFolderName = empresaFolderName || directoryEmpresa || empresaValue || cliente || '';
  const drivePathSegments = [
    clientFolderName,
    periodInfo.periodYear,
    'Impuestos',
    periodMonthFolderName,
    activityFolderName
  ].filter(Boolean);
  const drivePathSegmentsSafe = drivePathSegments.map(sanitizeFolderName).filter(Boolean);
  const fileMeta = {
    hasFiles: Boolean(hasFiles || fileCount || fileNames.length),
    fileCount: fileCount || fileNames.length,
    fileNames
  };
  const uploadPolicyNormalized = {
    allowDriveUpload: uploadPolicy?.allowDriveUpload !== false,
    maxFiles: Number.isFinite(uploadPolicy?.maxFiles) ? uploadPolicy.maxFiles : null
  };

  return {
    folio,
    cliente,
    empresa: empresaValue,
    Empresa: empresaValue,
    clienteAlias: aliasValue || '',
    Alias: aliasValue || '',
    alias: aliasValue || '',
    listaCtesEmpresa: empresaFolderName || directoryEmpresa,
    listaCtesEmpresaFolderName: empresaFolderName || directoryEmpresa,
    listaCtesEmpresaOriginal: directoryEmpresa,
    listaCtesIdCliente: directoryClientId,
    listaCtesAlias: directoryAlias,
    listaCtesNombre: directoryName,
    listaCtesInfo: directoryInfo,
    period: periodInfo.period,
    periodo: periodInfo.period,
    periodYear: periodInfo.periodYear,
    periodMonth: periodInfo.periodMonth,
    periodMonthName: periodMonthName || '',
    periodMonthFolderName,
    periodMonthFolderSafe: sanitizeFolderName(periodMonthFolderName),
    periodFolder: periodInfo.periodFolder,
    periodoYear: periodInfo.periodYear,
    periodoMonth: periodInfo.periodMonth,
    periodoFolder: periodInfo.periodFolder,
    clientFolderName,
    clientFolderNameSafe: sanitizeFolderName(clientFolderName),
    drivePathSegments,
    drivePathSegmentsSafe,
    service: service || obligation,
    obligation,
    folder,
    dueDate,
    dueLabel,
    dueText,
    requiresUpload: Boolean(request?.requiresUpload),
    actividadMensual,
    metadata: serviceMetadata,
    formInputs,
    serviceFieldLabels,
    emailRecipients,
    notificationPreview,
    uploadPolicy: uploadPolicyNormalized,
    fileStatus,
    existingFileStatus,
    existingFileRecordId,
    ...fileMeta,
    total,
    responsable,
    actualizado,
    contacto,
    clienteAsociadoId: directoryRecordId || clienteAsociadoId,
    clienteAsociadoRecordId: directoryRecordId,
    clienteAsociadoNombre: directoryName,
    airtableRecordId,
    ...restExtra
  };
}

export async function fetchN8nLogs(range) {
  if (!N8N_LOGS_URL) {
    throw new Error('Configura REACT_APP_N8N_LOGS_URL o REACT_APP_N8N_LOGS_UR para leer ejecuciones de n8n');
  }

  const rawRange = range || 'semana';
  const normalizedRange = RANGE_ALIAS[rawRange] || rawRange;
  const urlObj = new URL(N8N_LOGS_URL);
  urlObj.searchParams.set('range', rawRange);
  // Envia alias para cubrir flujos que esperan valores en inglés.
  if (normalizedRange !== rawRange) {
    urlObj.searchParams.set('rangeAlt', normalizedRange);
  }
  const url = urlObj.toString();

  const response = await fetch(url);

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`n8n logs error ${response.status}: ${message}`);
  }

  const rawBody = await response.text();
  const trimmedBody = rawBody.trim();

  if (!trimmedBody) {
    return [];
  }

  let parsed = null;
  try {
    parsed = JSON.parse(trimmedBody);
  } catch (error) {
    console.warn('[n8n] Respuesta de logs sin JSON, aplicando parseo personalizado');
  }

  if (parsed != null) {
    console.log('[n8n] Logs recibidos (JSON):', parsed);
    return getLogsArrayFromPayload(parsed) || [];
  }

  const fallbackLogs = parseLogsFromPlainText(trimmedBody);
  console.log('[n8n] Logs recibidos (texto plano):', fallbackLogs);
  return fallbackLogs;
}

export async function sendApprovedRequestFilesToN8n({
  xmlFile,
  pdfFile,
  files = [],
  request,
  webhookUrl,
  triggeredBy,
  executedAt
}) {

  const endpoint = resolveWebhookUrl(webhookUrl);
  const formData = new FormData();
  const primaryFileNames = [];
  const uploadPolicy = request?.uploadPolicy || {};
  const allowDriveUpload = uploadPolicy.allowDriveUpload !== false;
  const maxFilesAllowed =
    Number.isFinite(uploadPolicy.maxFiles) && uploadPolicy.maxFiles > 0 ? uploadPolicy.maxFiles : null;

  if (xmlFile) {
    formData.append('xmlFile', xmlFile, xmlFile.name);
    if (xmlFile.name) {
      primaryFileNames.push(xmlFile.name);
    }
  }
  if (pdfFile) {
    formData.append('pdfFile', pdfFile, pdfFile.name);
    if (pdfFile.name) {
      primaryFileNames.push(pdfFile.name);
    }
  }
  if (Array.isArray(files) && files.length && allowDriveUpload) {
    const limitedFiles = maxFilesAllowed ? files.slice(0, maxFilesAllowed) : files;
    limitedFiles.forEach(file => {
      if (file && file.name) {
        formData.append('files[]', file, file.name);
        primaryFileNames.push(file.name);
      }
    });
  }

  const fileNames = primaryFileNames.filter(Boolean);
  const fileCount = fileNames.length;
  const hasFiles = fileCount > 0;

  formData.append(
    'metadata',
    JSON.stringify(
      buildMetadataPayload(request, {
        triggeredBy,
        executedAt,
        fileNames,
        fileCount,
        hasFiles
      })
    )
  );

  console.log('[n8n] Enviando archivos aprobados', {
    folio: request?.folio,
    hasXml: Boolean(xmlFile),
    hasPdf: Boolean(pdfFile),
    files: fileNames,
    fileCount,
    endpoint
  });

  const response = await fetch(endpoint, {
    method: 'POST',
    body: formData
  });

  const rawBody = await response.text();
  const trimmedBody = rawBody.trim();

  if (!response.ok) {
    throw new Error(`n8n error ${response.status}: ${rawBody}`);
  }

  let parsedBody = null;
  try {
    parsedBody = trimmedBody ? JSON.parse(trimmedBody) : null;
  } catch (error) {
    if (trimmedBody) {
      console.warn('[n8n] Respuesta sin JSON, normalizando texto plano');
    }
  }

  let normalizedResponse;
  if (!trimmedBody) {
    normalizedResponse = {
      ok: true,
      status: 'ok',
      httpStatus: response.status,
      transport: 'empty-body'
    };
  } else if (parsedBody && typeof parsedBody === 'object' && !Array.isArray(parsedBody)) {
    normalizedResponse = {
      ok: true,
      httpStatus: response.status,
      transport: 'json',
      ...parsedBody,
      status: String(parsedBody.status || 'ok').trim() || 'ok'
    };
  } else {
    normalizedResponse = {
      ok: true,
      status: 'ok',
      httpStatus: response.status,
      transport: 'text',
      message: typeof parsedBody === 'string' ? parsedBody : trimmedBody,
      rawBody: trimmedBody
    };
  }

  console.log('[n8n] Flujo despachado correctamente', {
    folio: request?.folio,
    httpStatus: response.status,
    transport: normalizedResponse.transport,
    status: normalizedResponse.status
  });
  return normalizedResponse;
}

export async function sendCfdiSecureRequest({ payload, webhookUrl }) {
  const satSemantics = buildSatRequestSemantics({
    mode: payload?.request?.mode,
    tipoSolicitud: payload?.request?.tipoSolicitud,
    downloadType: payload?.request?.downloadType,
    requestType: payload?.request?.requestType,
    filters: payload?.request?.filters || {}
  });
  const payloadError = validateCfdiSecurePayload(payload || {});
  if (payloadError) {
    console.error('[CFDI][service] Payload invalido antes de enviar', {
      payloadError,
      clientRecordId: String(payload?.client?.recordId || payload?.clientRecordId || '').trim(),
      requestMode: satSemantics.requestMode,
      downloadType: satSemantics.downloadType,
      requestType: satSemantics.requestType
    });
    throw new Error(payloadError);
  }
  const configError = !webhookUrl ? getCfdiIntegrationConfigError() : '';
  if (configError) {
    console.error('[CFDI][service] Integracion CFDI incompleta', { configError });
    throw new Error(configError);
  }
  const endpoint = resolveCfdiRequestUrl(webhookUrl);
  const safePayload = {
    clientRecordId: String(payload?.client?.recordId || payload?.clientRecordId || '').trim(),
    legacyRequestMode: String(payload?.request?.mode || '').trim(),
    legacyRequestType: String(payload?.request?.tipoSolicitud || '').trim(),
    requestMode: satSemantics.requestMode,
    downloadType: satSemantics.downloadType,
    requestType: satSemantics.requestType,
    legacyModo: satSemantics.legacyModo,
    legacyTipoSolicitud: satSemantics.legacyTipoSolicitud,
    requestSemantics: {
      canonicalRequestMode: satSemantics.canonicalRequestMode,
      canonicalDownloadType: satSemantics.canonicalDownloadType,
      canonicalRequestType: satSemantics.canonicalRequestType,
      legacyRequestMode: satSemantics.legacyRequestMode,
      legacyRequestType: satSemantics.legacyRequestType,
      legacyDownloadType: satSemantics.legacyDownloadType
    },
    satSemanticsWarnings: satSemantics.warnings,
    filters: payload?.request?.filters || {},
    reuseExisting: Boolean(payload?.credentials?.reuseExisting),
    certificateSources: Array.isArray(payload?.credentials?.certificates)
      ? payload.credentials.certificates.map(cert => ({
          type: String(cert?.type || '').trim(),
          source: String(cert?.source || '').trim() || (String(cert?.existingAssetId || '').trim() ? 'existing' : 'upload'),
          hasExistingAssetId: Boolean(String(cert?.existingAssetId || '').trim()),
          hasContentBase64: Boolean(String(cert?.contentBase64 || '').trim()),
          hasSha256: Boolean(String(cert?.sha256 || '').trim())
        }))
      : [],
    hasPassphraseRef: Boolean(String(payload?.credentials?.passphraseRef || '').trim())
  };
  console.info('[CFDI][service] Envio iniciado', {
    endpoint,
    ...safePayload
  });
  if (satSemantics.warnings.length > 0) {
    console.warn('[CFDI][service] Semantica SAT con advertencias', {
      endpoint,
      clientRecordId: safePayload.clientRecordId,
      warnings: satSemantics.warnings,
      requestMode: satSemantics.requestMode,
      downloadType: satSemantics.downloadType,
      requestType: satSemantics.requestType
    });
  }
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload || {})
  });
  const data = await parseFetchResponse(response);
  console.info('[CFDI][service] Respuesta recibida', {
    endpoint,
    clientRecordId: safePayload.clientRecordId,
    httpOk: Boolean(response?.ok),
    responseKeys: Object.keys(data || {}),
    rawEstadoSolicitud: String(data?.estadoSolicitud || data?.status || '').trim(),
    errorCode: String(data?.errorCode || data?.code || '').trim(),
    errorMessage: String(data?.errorMessage || data?.message || '').trim(),
    idsPaquetesCount: Array.isArray(data?.idsPaquetes)
      ? data.idsPaquetes.length
      : Array.isArray(data?.ids_paquetes)
        ? data.ids_paquetes.length
        : Array.isArray(data?.packageIds)
          ? data.packageIds.length
          : 0
  });
  const shapeError = validateCfdiRunStatusShape(data);
  if (shapeError) {
    const normalizedPreview = normalizeCfdiRunStatus(data);
    console.error('[CFDI][service] Respuesta invalida del workflow', {
      endpoint,
      clientRecordId: safePayload.clientRecordId,
      shapeError,
      normalizedPreview,
      rawResponse: data
    });
    throw new Error(shapeError);
  }
  const normalized = normalizeCfdiRunStatus(data);
  console.info('[CFDI][service] Respuesta normalizada', {
    endpoint,
    clientRecordId: safePayload.clientRecordId,
    requestId: normalized.requestId,
    idSolicitudSat: normalized.idSolicitudSat,
    estadoSolicitud: normalized.estadoSolicitud,
    idsPaquetesCount: normalized.idsPaquetes.length,
    errorCode: normalized.errorCode,
    errorMessage: normalized.errorMessage,
    rawEstadoSolicitud: normalized.rawEstadoSolicitud,
    rawErrorMessage: normalized.rawErrorMessage
  });
  return normalized;
}

function buildCfdiStatusPayload({
  requestId,
  clientRecordId,
  idSolicitud,
  requestType,
  downloadType,
  mode
}) {
  return {
    mode: String(mode || 'request').trim().toLowerCase() || 'request',
    requestId: String(requestId || '').trim(),
    clientRecordId: String(clientRecordId || '').trim(),
    idSolicitud: String(idSolicitud || '').trim(),
    requestType: String(requestType || '').trim(),
    downloadType: String(downloadType || '').trim()
  };
}

export async function fetchCfdiRequestStatus({
  requestId,
  statusUrl,
  clientRecordId,
  idSolicitud,
  requestType,
  downloadType,
  mode
}) {
  if (!requestId) {
    throw new Error('requestId es obligatorio para consultar estado');
  }
  const configError = !statusUrl ? getCfdiIntegrationConfigError() : '';
  if (configError) {
    throw new Error(configError);
  }
  const baseUrl = statusUrl || getCfdiEndpointConfig().statusUrl;
  if (!baseUrl) {
    throw new Error('Configura REACT_APP_N8N_CFDI_STATUS_URL para consultar estado CFDI');
  }
  const endpoint = baseUrl.includes('{requestId}')
    ? baseUrl.replace('{requestId}', encodeURIComponent(requestId))
    : baseUrl;
  const payload = buildCfdiStatusPayload({
    requestId,
    clientRecordId,
    idSolicitud,
    requestType,
    downloadType,
    mode
  });
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });
  const data = await parseFetchResponse(response);
  const shapeError = validateCfdiRunStatusShape(data);
  if (shapeError) {
    throw new Error(shapeError);
  }
  return normalizeCfdiRunStatus(data);
}

function buildEfirmaControlRequestBody(payload = {}) {
  const certificates = Array.isArray(payload?.credentials?.certificates) ? payload.credentials.certificates : [];
  const cer = certificates.find(cert => cert?.type === 'cer') || {};
  const key = certificates.find(cert => cert?.type === 'key') || {};

  return {
    clientRecordId: String(payload?.client?.recordId || payload?.clientRecordId || '').trim(),
    recordId: String(payload?.client?.recordId || payload?.recordId || '').trim(),
    cerBase64: String(payload?.cerBase64 || cer?.contentBase64 || '').trim(),
    keyBase64: String(payload?.keyBase64 || key?.contentBase64 || '').trim(),
    cerSha256: String(payload?.cerSha256 || payload?.cerHash || cer?.sha256 || '').trim(),
    keySha256: String(payload?.keySha256 || payload?.keyHash || key?.sha256 || '').trim(),
    cerHash: String(payload?.cerSha256 || payload?.cerHash || cer?.sha256 || '').trim(),
    keyHash: String(payload?.keySha256 || payload?.keyHash || key?.sha256 || '').trim(),
    cerFileName: String(payload?.cerFileName || cer?.fileName || '').trim(),
    keyFileName: String(payload?.keyFileName || key?.fileName || '').trim(),
    passphraseRef: String(payload?.credentials?.passphraseRef || payload?.passphraseRef || '').trim(),
    requestId: String(payload?.metadata?.requestId || payload?.requestId || '').trim(),
    source: String(payload?.metadata?.source || payload?.source || 'ci-frontend-efirma-control-v1').trim(),
    requestedBy: String(payload?.metadata?.requestedBy || payload?.requestedBy || '').trim(),
    client: payload?.client || {},
    storage: payload?.storage || {},
    metadata: payload?.metadata || {}
  };
}

function mergeEfirmaResponseWithRequestMetadata(response = {}, requestBody = {}) {
  const cerSha256 = String(response?.cerSha256 || response?.cerHash || requestBody?.cerSha256 || '').trim();
  const keySha256 = String(response?.keySha256 || response?.keyHash || requestBody?.keySha256 || '').trim();
  const passphraseRef = String(response?.passphraseRef || requestBody?.passphraseRef || '').trim();
  if (!cerSha256 && !keySha256 && !passphraseRef) {
    return response;
  }
  return {
    ...response,
    cerSha256: response?.cerSha256 || response?.cerHash ? response.cerSha256 || response.cerHash : cerSha256,
    keySha256: response?.keySha256 || response?.keyHash ? response.keySha256 || response.keyHash : keySha256,
    passphraseRef: response?.passphraseRef || passphraseRef
  };
}

function getEfirmaWorkflowError(data = {}) {
  const explicitCode = String(data?.errorCode || data?.code || '').trim();
  const rawStatus = String(data?.status || '').trim();
  const statusMatch = /^ERROR:([A-Z0-9_:-]+)$/i.exec(rawStatus);
  const errorCode = explicitCode || (statusMatch ? String(statusMatch[1] || '').trim() : '');
  if (!errorCode) return null;
  const rawErrorMessage = String(data?.errorMessage || data?.message || `eFirma workflow error: ${errorCode}`).trim();
  const normalizedErrorMessage = /material existe pero falta metadata para reuso automatico/i.test(
    rawErrorMessage.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  )
    ? EFIRMA_REUSE_MISSING_METADATA_MESSAGE
    : rawErrorMessage;

  return {
    errorCode,
    errorMessage: normalizedErrorMessage,
    rawStatus
  };
}

function summarizeEfirmaProfile(profile = {}) {
  return {
    availability: profile?.availability || '',
    isAvailable: Boolean(profile?.isAvailable),
    isReusable: Boolean(profile?.isReusable),
    hasCerAssetId: Boolean(String(profile?.existingCerAssetId || '').trim()),
    hasKeyAssetId: Boolean(String(profile?.existingKeyAssetId || '').trim()),
    hasCerHash: Boolean(String(profile?.cerSha256 || '').trim()),
    hasKeyHash: Boolean(String(profile?.keySha256 || '').trim()),
    hasPassphraseRef: Boolean(String(profile?.passphraseRef || '').trim()),
    reuseChecklist: profile?.reuseChecklist || null,
    reuseBlockers: Array.isArray(profile?.reuseBlockers) ? profile.reuseBlockers : [],
    reuseStatusReason: String(profile?.reuseStatusReason || '').trim(),
    drivePath: profile?.drivePath || '',
    message: profile?.message || ''
  };
}

export async function sendEfirmaControlRequest({ payload, controlUrl }) {
  const configError = !controlUrl ? getEfirmaControlConfigError() : '';
  if (configError) {
    throw new Error(configError);
  }
  const endpoint = resolveEfirmaControlUrl(controlUrl);
  const requestBody = buildEfirmaControlRequestBody(payload);
  const safePayload = {
    clientRecordId: requestBody.clientRecordId,
    requestedBy: requestBody.requestedBy,
    certificateNames: [requestBody.cerFileName, requestBody.keyFileName].filter(Boolean)
  };
  console.info('[eFirma][service] Registro/actualizacion iniciada', {
    endpoint,
    ...safePayload,
    hasPassphraseRef: Boolean(requestBody.passphraseRef)
  });
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestBody)
  });
  const data = await parseFetchResponse(response);
  const workflowError = getEfirmaWorkflowError(data);
  if (workflowError) {
    console.error('[eFirma][service] Workflow rechazo el registro/actualizacion', {
      endpoint,
      clientRecordId: safePayload.clientRecordId,
      rawStatus: workflowError.rawStatus,
      errorCode: workflowError.errorCode,
      errorMessage: workflowError.errorMessage,
      fileCount: Array.isArray(data?.files) ? data.files.length : 0,
      writebackApplied: Boolean(data?.writebackApplied)
    });
    const error = new Error(workflowError.errorMessage || 'No fue posible registrar la eFirma.');
    error.code = workflowError.errorCode;
    error.errorCode = workflowError.errorCode;
    error.payload = data;
    throw error;
  }
  const profile = normalizeEfirmaProfile(mergeEfirmaResponseWithRequestMetadata(data, requestBody));
  console.info('[eFirma][service] Registro/actualizacion completada', {
    endpoint,
    clientRecordId: safePayload.clientRecordId,
    rawStatus: data?.status || '',
    fileCount: Array.isArray(data?.files) ? data.files.length : 0,
    writebackApplied: Boolean(data?.writebackApplied),
    responseKeys: Object.keys(data || {}),
    profile: summarizeEfirmaProfile(profile)
  });
  if (!profile.isReusable) {
    console.warn('[eFirma][service] Registro completado sin metadata suficiente para reuso', {
      clientRecordId: safePayload.clientRecordId,
      rawStatus: data?.status || '',
      profile: summarizeEfirmaProfile(profile)
    });
  }
  return profile;
}

export async function fetchEfirmaProfileStatus({ clientRecordId, statusUrl }) {
  if (!clientRecordId) {
    throw new Error('clientRecordId es obligatorio para consultar eFirma.');
  }
  const configError = !statusUrl ? getEfirmaStatusConfigError() : '';
  if (configError) {
    throw new Error(configError);
  }
  const baseUrl = statusUrl || getCfdiEndpointConfig().efirmaStatusUrl;
  const endpoint = baseUrl.includes('{clientRecordId}')
    ? baseUrl.replace('{clientRecordId}', encodeURIComponent(clientRecordId))
    : (() => {
        const url = new URL(baseUrl);
        url.searchParams.set('clientRecordId', clientRecordId);
        return url.toString();
      })();
  const response = await fetch(endpoint);
  const data = await parseFetchResponse(response);
  return normalizeEfirmaProfile(data);
}
